import { Asset } from '@flatsy/types/Assets'
import i18n from '@flatsy/utils/translations'

/**
 * @description build the final price with currency and currency suffix
 * @param asset Single asset object
 * @returns price formated with currency
 * @example "1000 € cc/m" || "1000 €"
 */
export function priceFormat(asset: Asset): string {
  if (asset.is_sale) {
    return `${Math.trunc(parseFloat(asset.price) || 0)} €`
  }

  return `${Math.trunc(
    parseFloat(asset.price) + parseFloat(asset.charges ? asset.charges : '0')
  )} € cc/m`
}

/**
 * @description build the final price of an asset
 * @param asset Single asset object
 * @returns final price
 */
export function priceValue(asset: Asset): number {
  if (asset.is_sale) {
    return Math.trunc(parseFloat(asset.price) || 0)
  }

  return Math.trunc(parseFloat(asset.price) + parseFloat(asset.charges || '0'))
}

/**
 * @description get the currency suffix
 * @param asset Single asset object
 * @returns currency suffix : cc/m
 */
export function priceCurrency(asset: Asset): string {
  if (!asset.is_sale) {
    return 'cc/m'
  }

  return ''
}

/**
 * @description convert number of floor into a plain text
 * @param floor number of floor
 * @returns the plain text floor
 */

const USRegionCodes = ['en', 'en-us']
export function floorFormat(value: number, compact?: boolean): string {
  const floor = USRegionCodes.indexOf(i18n.language?.toLowerCase()) > -1 ? value + 1 : value
  if (floor === 0) {
    return compact ? i18n.t('first_floor_compact') : i18n.t('first_floor')
  }
  return i18n.t('floor', {
    count: floor,
    ordinal: true,
  })
}

/**
 *
 * @description convert number of rooms into a plain text
 * @param rooms number of rooms
 * @returns the plain text rooms
 */
export function roomsFormat(rooms: number): string {
  switch (rooms) {
    case 0:
      return `${rooms} ${i18n.t('room')}`
    case 1:
      return `${rooms} ${i18n.t('room')}`
    default:
      return `${rooms} ${i18n.t('rooms')}`
  }
}

export function bedroomsFormat(rooms: number): string {
  switch (rooms) {
    case 0:
      return `${rooms} ${i18n.t('bedroom')}`
    case 1:
      return `${rooms} ${i18n.t('bedroom')}`
    default:
      return `${rooms} ${i18n.t('bedrooms')}`
  }
}

/**
 * @description return surface with m² suffix
 * @param surface number
 * @returns plain text surface
 */
export function surfaceFormat(surface: number): string {
  return `${Math.trunc(surface)} m²`
}

export function cleanAddressState(address?: string): string {
  if (!address) {
    return ''
  }
  // keep this line to save it
  // const splited = address.split(",");
  // return splited.splice(0, splited.length - 1).join(",");
  return address
}

/**
 * @name shortAddress
 * @description convert address exemple 2 Rue Jos Kieffer, Esch-sur-Alzette, Luxembourg to Rue Jos Kieffer
 * @param {string} address
 * @return {*}  {string}
 */
export function shortAddress(address: string): string {
  const getFirstPart = address.split(',')[0]
  const removeNumber = getFirstPart.replace(/[0-9]/g, '')

  return removeNumber
}

// TODO: TEMPORARY delete when translations done
/**
 * @description convert key base string into plain text
 * @param type professional Status
 * @returns plain text for type
 * @example "alone" => "J'emménage seul"
 */
export function householdType(type: string): string {
  switch (type) {
    case 'alone':
      return i18n.t('i_am_moving_in_alone')
    case 'roommates':
      return i18n.t('i_am_moving_into_a_roommate')
    case 'family':
      return i18n.t('i_am_moving_in_with_family')
    default:
      return i18n.t('i_am_moving_in_alone')
  }
}

/**
 * @description convert key base string into plain text
 * @param type professional Status
 * @returns plain text for type
 * @example "cdi_confirmed" => "CDI confirmé"
 */

export function professionalStatus(type: string): string {
  switch (type) {
    case 'cdi_confirmed':
      return i18n.t('confirmed_permanent_contract')
    case 'cdi_trial':
      return i18n.t('permanent_contract_in_trial_period')
    case 'cdd_short':
      return i18n.t('cdd_short')
    case 'cdd_long':
      return i18n.t('cdd_long')
    case 'student':
      return i18n.t('student_or_intern')
    case 'civil_servant':
      return i18n.t('civil_servant')
    case 'entrepreneur':
      return i18n.t('business_manager')
    case 'liberal_profession':
      return i18n.t('liberal_or_independent_profession')
    case 'retired':
      return i18n.t('retired')
    case 'unemployed':
      return i18n.t('unemployed')
    default:
      return i18n.t('confirmed_permanent_contract')
  }
}
/**
 * @description convert true or false into "Oui" | "Non"
 * @param type "true" | "false"
 * @returns "Oui" | "Non"
 */
export function isSelling(type: string): string {
  switch (type) {
    case 'true':
      return i18n.t('yes')
    default:
      return i18n.t('no')
  }
}

/**
 * @description convert key base string into plain text
 * @param type purchase type
 * @returns plain text for type
 * @example "primary_residence" => "Résidence principale"
 */
export function purchaseMotive(type: string): string {
  switch (type) {
    case 'primary_residence':
      return i18n.t('main_residence')
    case 'rental_investment':
      return i18n.t('rental_investment')
    case 'secondary_residence':
      return i18n.t('secondary_residence')
    default:
      return i18n.t('main_residence')
  }
}
